import React, { useEffect, useRef } from "react";

import { useMapStore } from "stores/map";
import type { WellNetworkRequest } from "stores/map";
import {
  selectNetworkDepthLegend,
  selectSelectedWells,
  selectShowNetworkOptions,
  selectWellNetworkMapRequest
} from "stores/map/mapSelectors";

import Icon_MidstreamNetwork from "components/icons/Icon_MidstreamNetwork";
import { useClearWellToFacilityNetwork } from "components/map/hooks/useClearWellToFacilityNetwork";
import { useMapContext } from "components/map/hooks/useMapContext";
import { useShowWellToFacilityNetwork } from "components/map/hooks/useShowWellToFacilityNetwork";
import {
  midstreamNavigatorActions,
  useMidstreamNavigatorContext
} from "components/midstream/midstream-navigator/context/MidstreamNavigatorContext";

import { FilterKey, useFilterButtonContext } from "../context/FilterButtonContext";
import { BaseMidstreamFilterButton, FilterConfig } from "../filters/BaseFilterButton";
import { useNetworkBoundsManager } from "../hooks/useNetworkBoundsManager";

export const WellNetworkButton: React.FC = () => {
  const [state, dispatch] = useMidstreamNavigatorContext();
  const { mapbox } = useMapContext();
  const { getIsDisabled, withUserAction } = useFilterButtonContext();

  // Zustand map store: reads
  const selectedWells = useMapStore(selectSelectedWells);
  const wellNetworkMapRequest = useMapStore(selectWellNetworkMapRequest);
  const showNetworkOptions = useMapStore(selectShowNetworkOptions);
  const networkDepthLegend = useMapStore(selectNetworkDepthLegend);

  // Zustand map store: actions
  const setWellNetworkMapRequest = useMapStore((s) => s.setWellNetworkMapRequest);
  const setWellNetworkFilterActivated = useMapStore(
    (s) => s.setWellNetworkFilterActivated
  );

  const { clearEnvelopes } = useNetworkBoundsManager(mapbox);

  const networkEverShown = useRef(false);
  const networkUserDeactivated = useRef(false);

  const setWellNetworkMapRequestAction = (request: WellNetworkRequest) => {
    setWellNetworkMapRequest(request);
  };

  const { showWellToFacilityNetwork } = useShowWellToFacilityNetwork({
    wellNetworkMapRequest,
    setWellNetworkMapRequest: setWellNetworkMapRequestAction
  });

  const { clearWellToFacilityNetwork } = useClearWellToFacilityNetwork({ mapbox });

  const config: FilterConfig = {
    icon: <Icon_MidstreamNetwork />,
    tooltip: "Show Well to Facility Network for Selected Well",
    testId: `filter-button-${FilterKey.WellNetwork}`
  };

  const isActive = state.activeFilters?.includes(FilterKey.WellNetwork) ?? false;
  const isDisabled = getIsDisabled(FilterKey.WellNetwork, isActive);
  const disabledReason = isDisabled ? "Select a well to display network" : null;

  useEffect(() => {
    if (isDisabled && isActive) {
      dispatch(midstreamNavigatorActions.removeFilter(FilterKey.WellNetwork));
      setWellNetworkFilterActivated(false);
      clearWellToFacilityNetwork();
      clearEnvelopes();
    }
  }, [
    isDisabled,
    isActive,
    dispatch,
    setWellNetworkFilterActivated,
    clearWellToFacilityNetwork,
    clearEnvelopes
  ]);

  useEffect(() => {
    if (isActive && selectedWells && Object.keys(selectedWells).length > 0) {
      const currentWellIds = Object.keys(selectedWells);

      const currentRequestWellIds = wellNetworkMapRequest.wellIds || [];
      const hasWellsChanged =
        currentWellIds.length !== currentRequestWellIds.length ||
        !currentWellIds.every((id) => currentRequestWellIds.includes(id));

      if (hasWellsChanged && currentRequestWellIds.length > 0) {
        setWellNetworkMapRequest({
          ...wellNetworkMapRequest,
          wellIds: currentWellIds
        });
        const wellId = currentWellIds[0];
        showWellToFacilityNetwork(wellId);
      }
    }
  }, [selectedWells, isActive, wellNetworkMapRequest]);

  useEffect(() => {
    if (showNetworkOptions && networkDepthLegend.visible) {
      networkEverShown.current = true;
    }
  }, [showNetworkOptions, networkDepthLegend.visible]);

  const prevShowNetworkOptions = useRef(false);
  const prevNetworkDepthLegendVisible = useRef(false);

  useEffect(() => {
    if (networkUserDeactivated.current) {
      networkUserDeactivated.current = false;
      prevShowNetworkOptions.current = showNetworkOptions;
      prevNetworkDepthLegendVisible.current = networkDepthLegend.visible;
      return;
    }

    const isNetworkOptionsClosedByUser =
      prevShowNetworkOptions.current && !showNetworkOptions;
    const isDepthLegendClosedByUser =
      prevNetworkDepthLegendVisible.current && !networkDepthLegend.visible;

    const isLikelySystemClosure = isDepthLegendClosedByUser && showNetworkOptions;
    if (
      isActive &&
      (isNetworkOptionsClosedByUser || isDepthLegendClosedByUser) &&
      !isLikelySystemClosure
    ) {
      dispatch(midstreamNavigatorActions.removeFilter(FilterKey.WellNetwork));
      setWellNetworkMapRequest({
        ...wellNetworkMapRequest,
        wellIds: []
      });
      networkEverShown.current = false;
    }

    prevShowNetworkOptions.current = showNetworkOptions;
    prevNetworkDepthLegendVisible.current = networkDepthLegend.visible;
  }, [isActive, showNetworkOptions, networkDepthLegend.visible, dispatch]);

  const handleToggle = () => {
    withUserAction(() => {
      if (!isActive) {
        const hasSelectedWells = selectedWells && Object.keys(selectedWells).length > 0;
        if (hasSelectedWells) {
          dispatch(midstreamNavigatorActions.addFilter(FilterKey.WellNetwork));
          setWellNetworkFilterActivated(true);
          const wellId = Object.keys(selectedWells)[0];
          showWellToFacilityNetwork(wellId);
        }
      } else {
        networkUserDeactivated.current = true;
        dispatch(midstreamNavigatorActions.removeFilter(FilterKey.WellNetwork));
        setWellNetworkFilterActivated(false);
        clearWellToFacilityNetwork();
        clearEnvelopes();
      }
    });
  };

  return (
    <BaseMidstreamFilterButton
      config={config}
      isActive={isActive}
      isDisabled={isDisabled}
      disabledReason={disabledReason}
      onToggle={handleToggle}
    />
  );
};
